import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  memoriesAuthHandoffUrl,
  sanitizeMemoriesReturnUrl,
  isMemoriesLogoutInProgress,
  clearMemoriesLogoutGuard,
} from '../utils/memoriesAuthBridge';

/**
 * Main-app bridge: if the traveler is signed in, hand the same JWT to the
 * memories host. Otherwise send them through the normal /login flow first.
 */
const MemoriesAuthBridge = () => {
  const { user, loading, isTokenExpired, logout } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnUrl = sanitizeMemoriesReturnUrl(params.get('return'));

  useEffect(() => {
    if (loading) return undefined;

    // Logout in progress: never hand a JWT back to Memories.
    if (isMemoriesLogoutInProgress()) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      logout();
      clearMemoriesLogoutGuard();
      navigate('/login', { replace: true });
      return undefined;
    }

    const token = localStorage.getItem('token');
    if (user && token && !isTokenExpired(token)) {
      window.location.replace(memoriesAuthHandoffUrl(token, returnUrl));
      return undefined;
    }

    const next = `/auth/memories-bridge?return=${encodeURIComponent(returnUrl)}`;
    navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    return undefined;
  }, [loading, user, isTokenExpired, returnUrl, navigate, logout]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="50vh"
      gap={2}
    >
      <CircularProgress size={36} />
      <Typography variant="body2" color="text.secondary">
        Continuing to Memories…
      </Typography>
    </Box>
  );
};

export default MemoriesAuthBridge;
